import React, { useCallback, useRef, useState } from 'react'
import { useEditorState } from '@tiptap/react'
import { Popover, Flex, Button } from '@radix-ui/themes'
import { isAllowedUri } from '@tiptap/extension-link'
import clsx from 'clsx'
import { formatSeconds } from '@disco/utils/time-utils'
import type { ToolbarItem } from '../../types'
import { toPlainText } from '../../serializers/plain-text'
import { UrlInputDialog } from './dialogs/url-input-dialog'
import { CtaButtonDialog } from './dialogs/cta-button-dialog'
import { ImageInsertDialog } from './dialogs/image-insert-dialog'
import {
  FontSizeSelect,
  TextTypeSelect,
  ColorPickerButton,
  MergeTagsMenu,
} from './toolbar-controls'
import { ITEM_GROUPS } from './defaults'
import type { ToolbarProps } from './types'
import { ToolbarButton } from './toolbar-button'
import { insertWithBoundarySpaces, isDocBlank, resolveItems } from './utils'
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  BulletListIcon,
  OrderedListIcon,
  AlignLeftIcon,
  AlignCenterIcon,
  AlignRightIcon,
  LinkIcon,
  UnlinkIcon,
  EditIcon,
  ImageIcon,
  AtIcon,
  TimestampIcon,
  TimeRangeIcon,
  CtaIcon,
} from './icons'
import styles from './toolbar.module.css'
import { isApple } from '@disco/utils/browser'

export function Toolbar({
  editor,
  mode,
  customToolbar,
  onSubmit,
  onCancel,
  onMentionTrigger,
  onImageUpload,
  getCurrentTimestamp,
  isTimestampActive = false,
  submitText = 'Submit',
  showDividers = true,
  maxLength,
  isSubmitting = false,
}: ToolbarProps) {
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [linkDefaultText, setLinkDefaultText] = useState('')
  const [linkDefaultUrl, setLinkDefaultUrl] = useState('')
  const [imageDialogOpen, setImageDialogOpen] = useState(false)
  const [ctaDialogOpen, setCtaDialogOpen] = useState(false)
  const linkRangeRef = useRef<{ from: number; to: number } | null>(null)

  const openLinkDialog = useCallback(() => {
    const { from, to } = editor.state.selection
    linkRangeRef.current = from === to ? null : { from, to }
    setLinkDefaultText(
      from === to ? '' : editor.state.doc.textBetween(from, to, ' '),
    )
    setLinkDefaultUrl('')
    setLinkDialogOpen(true)
  }, [editor])

  const openEditLinkDialog = useCallback(() => {
    editor.chain().focus().extendMarkRange('link').run()
    const { from, to } = editor.state.selection
    linkRangeRef.current = from === to ? null : { from, to }
    setLinkDefaultText(editor.state.doc.textBetween(from, to, ' '))
    setLinkDefaultUrl((editor.getAttributes('link').href as string) ?? '')
    setLinkDialogOpen(true)
  }, [editor])

  const handleUnlink = useCallback(() => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run()
  }, [editor])

  const editorState = useEditorState({
    editor,
    selector: ({ editor: ed }) => ({
      isBold: ed.isActive('bold'),
      isItalic: ed.isActive('italic'),
      isUnderline: ed.isActive('underline'),
      isBulletList: ed.isActive('bulletList'),
      isOrderedList: ed.isActive('orderedList'),
      isLink: ed.isActive('link'),
      alignLeft: ed.isActive({ textAlign: 'left' }),
      alignCenter: ed.isActive({ textAlign: 'center' }),
      alignRight: ed.isActive({ textAlign: 'right' }),
      isBlank: isDocBlank(ed),
      // Match `body_raw_input` and the BE's Python len() exactly: serialize to
      // plain text (already trims boundaries) and count Unicode code points.
      characters: maxLength != null ? [...toPlainText(ed.getJSON())].length : 0,
    }),
  })

  const showCharacterCount = maxLength != null
  const isOverLimit = showCharacterCount && editorState.characters > maxLength

  const handleMention = useCallback(() => {
    if (onMentionTrigger) {
      onMentionTrigger()
      return
    }
    insertWithBoundarySpaces(editor, '@', { cursorBeforeTrailingSpace: true })
  }, [editor, onMentionTrigger])

  const handleInsertTimestamp = useCallback(() => {
    const now = getCurrentTimestamp?.() ?? 0
    insertWithBoundarySpaces(editor, formatSeconds(now), {
      alwaysTrailingSpace: true,
    })
  }, [editor, getCurrentTimestamp])

  const handleInsertTimeRange = useCallback(() => {
    const now = getCurrentTimestamp?.() ?? 0
    const tc = formatSeconds(now)
    insertWithBoundarySpaces(editor, `${tc}-${tc}`, {
      alwaysTrailingSpace: true,
    })
  }, [editor, getCurrentTimestamp])

  const mod = isApple ? '⌘' : 'Ctrl'

  const items = resolveItems(mode, customToolbar)

  const renderItem = (item: ToolbarItem): React.ReactNode => {
    switch (item) {
      case 'bold':
        return (
          <ToolbarButton
            key={item}
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editorState.isBold}
            title={`Bold (${mod}+B)`}
            ariaLabel="Bold"
          >
            <BoldIcon />
          </ToolbarButton>
        )
      case 'italic':
        return (
          <ToolbarButton
            key={item}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editorState.isItalic}
            title={`Italic (${mod}+I)`}
            ariaLabel="Italic"
          >
            <ItalicIcon />
          </ToolbarButton>
        )
      case 'underline':
        return (
          <ToolbarButton
            key={item}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            active={editorState.isUnderline}
            title={`Underline (${mod}+U)`}
            ariaLabel="Underline"
          >
            <UnderlineIcon />
          </ToolbarButton>
        )
      case 'bullet-list':
        return (
          <ToolbarButton
            key={item}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editorState.isBulletList}
            title="Bullet list"
            ariaLabel="Bullet list"
          >
            <BulletListIcon />
          </ToolbarButton>
        )
      case 'ordered-list':
        return (
          <ToolbarButton
            key={item}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            active={editorState.isOrderedList}
            title="Numbered list"
            ariaLabel="Numbered list"
          >
            <OrderedListIcon />
          </ToolbarButton>
        )
      case 'text-align':
        return (
          <React.Fragment key={item}>
            <ToolbarButton
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
              active={editorState.alignLeft}
              title="Align left"
              ariaLabel="Align left"
            >
              <AlignLeftIcon />
            </ToolbarButton>
            <ToolbarButton
              onClick={() =>
                editor.chain().focus().setTextAlign('center').run()
              }
              active={editorState.alignCenter}
              title="Align center"
              ariaLabel="Align center"
            >
              <AlignCenterIcon />
            </ToolbarButton>
            <ToolbarButton
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
              active={editorState.alignRight}
              title="Align right"
              ariaLabel="Align right"
            >
              <AlignRightIcon />
            </ToolbarButton>
          </React.Fragment>
        )
      case 'link':
        if (editorState.isLink) {
          return (
            <Popover.Root key={item}>
              <Popover.Trigger>
                <ToolbarButton
                  active
                  title="Edit or remove link"
                  ariaLabel="Edit or remove link"
                >
                  <LinkIcon />
                </ToolbarButton>
              </Popover.Trigger>
              <Popover.Content size="1">
                <Flex gap="1">
                  <Popover.Close>
                    <ToolbarButton
                      onClick={openEditLinkDialog}
                      title="Edit link"
                      ariaLabel="Edit link"
                    >
                      <EditIcon />
                    </ToolbarButton>
                  </Popover.Close>
                  <Popover.Close>
                    <ToolbarButton
                      onClick={handleUnlink}
                      title="Remove link"
                      ariaLabel="Remove link"
                    >
                      <UnlinkIcon />
                    </ToolbarButton>
                  </Popover.Close>
                </Flex>
              </Popover.Content>
            </Popover.Root>
          )
        }
        return (
          <ToolbarButton
            key={item}
            onClick={openLinkDialog}
            active={false}
            title="Insert link"
            ariaLabel="Insert link"
          >
            <LinkIcon />
          </ToolbarButton>
        )
      case 'image':
        return (
          <ToolbarButton
            key={item}
            onClick={() => setImageDialogOpen(true)}
            title="Insert image"
            ariaLabel="Insert image"
          >
            <ImageIcon />
          </ToolbarButton>
        )
      case 'heading':
        return <TextTypeSelect key={item} editor={editor} />
      case 'font-size':
        return <FontSizeSelect key={item} editor={editor} />
      case 'text-color':
        return (
          <ColorPickerButton
            key={item}
            editor={editor}
            type="text"
            title="Text color"
            ariaLabel="Text color"
          />
        )
      case 'bg-color':
        return (
          <ColorPickerButton
            key={item}
            editor={editor}
            type="background"
            title="Background color"
            ariaLabel="Background color"
          />
        )
      case 'merge-tag':
        return <MergeTagsMenu key={item} editor={editor} />
      case 'cta-button':
        return (
          <ToolbarButton
            key={item}
            onClick={() => setCtaDialogOpen(true)}
            title="Insert CTA button"
            ariaLabel="Insert CTA button"
          >
            <CtaIcon />
          </ToolbarButton>
        )
      case 'mention':
        return (
          <ToolbarButton
            key={item}
            onClick={handleMention}
            title="Mention someone (@)"
            ariaLabel="Mention someone"
          >
            <AtIcon />
          </ToolbarButton>
        )
      case 'timestamp': {
        const label = isTimestampActive
          ? 'Insert current timestamp'
          : 'Insert a timestamp'
        return (
          <ToolbarButton
            key={item}
            onClick={handleInsertTimestamp}
            title={label}
            ariaLabel={label}
          >
            <TimestampIcon />
          </ToolbarButton>
        )
      }
      case 'timestamp-range':
        return (
          <ToolbarButton
            key={item}
            onClick={handleInsertTimeRange}
            title="Insert timestamp range"
            ariaLabel="Insert timestamp range"
          >
            <TimeRangeIcon />
          </ToolbarButton>
        )
      case 'submit':
        return (
          <Flex key={item} gap="2" align="center">
            {onCancel && (
              <Button
                variant="ghost"
                color="gray"
                type="button"
                onClick={onCancel}
                title="Cancel"
                aria-label="Cancel"
                mr="2"
                className={styles.cancelButton}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            )}
            <Button
              variant="solid"
              type="button"
              onClick={onSubmit}
              className={styles.submitButton}
              disabled={editorState.isBlank || isOverLimit || isSubmitting}
              title={submitText}
              aria-label={submitText}
            >
              {submitText}
            </Button>
          </Flex>
        )
      default:
        return null
    }
  }

  const submitIndex = items.indexOf('submit')
  const nonSubmitItems =
    submitIndex >= 0 ? items.filter(item => item !== 'submit') : items
  const hasSubmit = submitIndex >= 0
  const hasRightCluster = hasSubmit || showCharacterCount

  const nodes: React.ReactNode[] = []
  let prevGroup: string | null = null
  nonSubmitItems.forEach((item, idx) => {
    const group = ITEM_GROUPS[item]
    if (showDividers && idx > 0 && group !== prevGroup) {
      nodes.push(<div key={`div-${idx}`} className={styles.divider} />)
    }
    nodes.push(renderItem(item))
    prevGroup = group
  })

  const counterNode = showCharacterCount ? (
    <span
      className={clsx(
        styles.characterCount,
        isOverLimit && styles.characterCountOver,
      )}
      aria-live="polite"
    >
      {editorState.characters} / {maxLength}
    </span>
  ) : null

  return (
    <div
      className={hasRightCluster ? styles.discussionToolbar : styles.toolbar}
    >
      {hasRightCluster ? (
        <>
          <div className={styles.toolbarGroup}>{nodes}</div>
          <Flex align="center">
            {counterNode}
            {hasSubmit && renderItem('submit')}
          </Flex>
        </>
      ) : (
        <>{nodes}</>
      )}

      <UrlInputDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        title="Insert Link"
        description="Enter the URL for the link."
        placeholder="https://example.com"
        validate={url => Boolean(isAllowedUri(url))}
        errorMessage="Please enter a valid URL starting with http://, https://, or mailto:"
        showTextField
        defaultText={linkDefaultText}
        defaultUrl={linkDefaultUrl}
        textPlaceholder="Link text (defaults to URL)"
        onSubmit={(url, text) => {
          const display = text?.trim() || url
          const range = linkRangeRef.current
          if (range && display === linkDefaultText) {
            editor
              .chain()
              .focus()
              .setTextSelection(range)
              .setLink({ href: url })
              .setTextSelection(range.to)
              .unsetMark('link')
              .run()
            return
          }
          const { from } = editor.state.selection
          editor
            .chain()
            .focus()
            .insertContent({
              type: 'text',
              text: display,
              marks: [{ type: 'link', attrs: { href: url } }],
            })
            .setTextSelection(from + display.length)
            .unsetMark('link')
            .run()
        }}
      />

      <ImageInsertDialog
        open={imageDialogOpen}
        onOpenChange={setImageDialogOpen}
        editor={editor}
        onImageUpload={onImageUpload}
      />
      {mode === 'email' && (
        <CtaButtonDialog
          open={ctaDialogOpen}
          onOpenChange={setCtaDialogOpen}
          editor={editor}
        />
      )}
    </div>
  )
}
